import { useEffect, useRef, useState, type ReactElement } from 'react';
import { toast } from '../../../design-system/components';
import { track, trackFailed } from '../../../../../shared-web/track';
import { ApiError } from '../../auth/api-client';
import { apiFailureReason, emberApi, isAuthRequired, type ConversationMessage, type RoomLine, type RoomPageResponse } from '../api';
import { ConversationShell } from '../components/ConversationShell';
import { PageFailure } from '../components/PageFailure';
import { PageLoading } from '../components/PageLoading';
import { PresenceAvatar } from '../components/PresenceAvatar';
import { countWord } from '../format';
import { useRoomStream, type RoomNote } from '../components/room-stream';
import { Modal } from '../components/Modal';
import { RoomReactionBar, RoomReactionCapsules, RoomReaders } from '../components/RoomReactions';
import { QUICK_COUNT, QUICK_FALLBACK, readersByLine, setLineReactions, withReaction } from '../components/room-reactions';
import { notifyKnocksChanged } from '../components/use-knocks';
import { navigate } from '../router';
import { PAIR_RULES, ROOM_RULES, roomTimer } from '../room-timer';

/** The note that came with the page waits this long after the thread is drawn (2026-10-17 Riley: too fast). */
const PAGE_NOTE_DELAY_MS = 1200;
import './RoomThread.css';

function whenBack(at: string): string {
  const d = new Date(at);
  const now = new Date();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  if (d.toDateString() === now.toDateString()) return `today ${time}`;
  if (d.toDateString() === tomorrow.toDateString()) return `tomorrow ${time}`;
  return `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${time}`;
}

/** A room's line as a turn of the same thread the conversation uses; `seats` the others in seat order (a name's colour). */
function toMessage(l: RoomLine, seats: string[]): ConversationMessage {
  if (l.kind === 'system') return { role: 'system', content: l.text, turn_index: l.seq };
  if (l.kind === 'host') return { role: 'assistant', content: l.text, turn_index: l.seq, created_at: l.at ?? undefined, face: 'listening' };
  if (l.mine) return { role: 'user', content: l.text, turn_index: l.seq, created_at: l.at ?? undefined };
  const seat = seats.indexOf(l.author || '');
  return { role: 'peer', content: l.text, turn_index: l.seq, created_at: l.at ?? undefined, speaker: { name: l.author || 'Someone', avatar: l.avatar, seat: seat >= 0 ? seat : 0 } };
}

/**
 * A room inside the talk page (§4.21, 2026-09-25 二次拍板; the head 三次拍板):
 * the same thread the conversation uses — Ember beside its lines, the
 * others by the names that exist here, this person on the right — with one
 * line over it: who is here, with Ember. That line unfolds into everything
 * the room has to say about itself: each seat with the line on it (the
 * sentence approved at the door; 2026-10-04 二次拍板), the person's own name
 * (theirs to change), why the door opened,
 * the way out. Folded until tapped (2026-10-01 拍板; it opened on the
 * first visit from 09-30, and a first visit then looked like a different page).
 * Before the first look (2026-10-01 二次拍板) the door: the name they carry
 * here — theirs across rooms, the pool's word only the very first time — with
 * a pencil, and `Go in`; the room sees them (me.seen) only once they go in,
 * and the door stays shut while someone seated already goes by their name.
 * While the invitation waits, or after leaving or the close, the room's own
 * page (/rooms/:id) is the place.
 *
 * Coming back (2026-10-22 jam): the lines from the others since this person
 * last read are under a divider, and nothing floats over them. From 10-09 Ember
 * left a note on coming back ("While you were away", Show me) — over the very
 * lines it pointed at. The one note left is the room's last day (From Ember, ×).
 */
export function RoomThread({ roomId }: { roomId: string }): ReactElement {
  const [page, setPage] = useState<RoomPageResponse | null>(null);
  const [held, setHeld] = useState('');
  const [failed, setFailed] = useState<false | 'gone' | 'lapsed' | 'load_failed'>(false);
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState<string | null>(null);
  const [introing, setIntroing] = useState<string | null>(null);   // the line on their seat, open for editing at the door
  const [clash, setClash] = useState(false);   // the door did not open: someone seated here already goes by this name
  const [leaving, setLeaving] = useState(false);
  // The choices open in view (2026-10-27 walk-through: on a phone they opened under the fold's height, half hidden).
  const leaveRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { if (leaving) leaveRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, [leaving]);
  const [busy, setBusy] = useState(false);
  // The room's rules (2026-10-17 Riley, the App the same): a ? beside the timer brings them up as a bubble that
  // takes no room — the thread is anchored to its foot, so anything growing at the top would push the ? away.
  // Only the ? opens it (2026-10-20 founder: up by itself on coming in, it covered the room).
  const [rules, setRules] = useState(false);
  const seenRef = useRef(false);

  useEffect(() => {
    seenRef.current = false;
    setFailed(false);
    setPage(null);
    setNewAfter(null);
    emberApi.room(roomId)
      .then((r) => {
        // The thread is for a seated member of an open room; every other
        // state (invited, waiting, left, closed) is the room's own page.
        if (!r.me.joined || r.me.left || r.room.status !== 'open') { navigate(`/rooms/${encodeURIComponent(roomId)}`, { replace: true }); return; }
        setPage(r);
        // Where this person had read to, when lines from the others came after it: the divider (2026-10-22 jam).
        // Never for a first visit, when nothing was read yet (2026-10-27 walk-through: "NEW SINCE YOU WERE HERE" above a
        // newcomer's welcome and a new friend's first line).
        if (r.me.seen && r.me.read_seq > 0 && r.lines.some((l) => (l.kind === 'human' || l.kind === 'host') && !l.mine && l.seq > r.me.read_seq)) setNewAfter(r.me.read_seq);
        // A note that came with the page (the room's last day), not waiting on Stream (2026-10-17); it comes up a
        // moment after the thread is drawn, so it is seen arriving rather than flashing in with the page.
        const pageNote = r.me.note;
        if (pageNote?.text) window.setTimeout(() => setNote({ text: pageNote.text, afterSeq: Number(pageNote.after_seq) || 0 }), PAGE_NOTE_DELAY_MS);
        if (!seenRef.current) {
          seenRef.current = true;
          track('ui.clicked', { name: 'room_open', state: 'open', match_id: roomId });
          // A first look is the door's to give (enter below); every later open is a coming back (2026-10-09).
          if (r.me.seen) emberApi.roomSeen(roomId, true).then(() => notifyKnocksChanged()).catch(() => { /* next time */ });
        }
      })
      .catch((err: unknown) => {
        // The session ran out under the thread (2026-10-04): the sign-in
        // door, and the room again after it.
        if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent(`/talk?mode=chat&room=${roomId}`)}`, { replace: true }); return; }
        const gone = err instanceof ApiError && err.status === 404;
        if (!gone) trackFailed('room_load', 'load_failed', { error_detail: apiFailureReason(err) });
        // A room that never came together says so (2026-10-18; edge room_lapsed, still a 404).
        const lapsed = gone && (err.body as { error?: string } | null)?.error === 'room_lapsed';
        setFailed(lapsed ? 'lapsed' : gone ? 'gone' : 'load_failed');
      });
  }, [roomId]);

  // The thread, live (2026-09-29): a line the moment it lands, a line taken
  // down the moment it goes, the mute, the close — from the room's Stream
  // channel. Every other state (closed, closed to them) is the room's page.
  const [typing, setTyping] = useState<string[]>([]);
  const [deleting, setDeleting] = useState<RoomLine | null>(null);  // my line a Delete was tapped on, awaiting the word (2026-09-30)
  const [reporting, setReporting] = useState<RoomLine | null>(null); // another's line a Report was tapped on, awaiting the word
  // Ember's note on the room's last day (2026-10-17), and where this person had read to on coming in — the divider.
  const [note, setNote] = useState<RoomNote | null>(null);
  // The quick bar over a held line (需求四): the line and the bubble it floats over.
  const [holding, setHolding] = useState<{ line: RoomLine; el: HTMLElement } | null>(null);
  const [newAfter, setNewAfter] = useState<number | null>(null);
  const live = useRoomStream(roomId, Boolean(page?.me.seen), setPage, () => navigate(`/rooms/${encodeURIComponent(roomId)}`, { replace: true }), setTyping, setNote);

  if (failed === 'lapsed') return <PageFailure title="This room didn’t come together." detail="Not enough people said yes." logState="lapsed" />;
  if (failed === 'gone') return <PageFailure title="This room isn’t here." detail="It may have closed to you, or the link is old." logState="not_found" />;
  if (failed) return <PageFailure title="Couldn’t open this." detail="Try again in a moment." logState="load_failed" />;
  if (!page) return <PageLoading variant="inline" />;

  const { room, me, members } = page;
  const muted = Boolean(me.muted_until && new Date(me.muted_until) > new Date());
  const others = members.filter((m) => !m.me).map((m) => m.name);
  const typingShown = typing.filter((name) => !me.blocked?.includes(name));
  const names = others.length ? `${others.join(', ')} & you` : 'you';
  // Two new friends (2026-10-26, New friends): the room is named for the other person, with Ember.
  const pair = room.kind === 'pair';

  const send = async (text: string): Promise<boolean> => {
    setHeld('');
    live.stopTyping();
    try {
      const out = await emberApi.sayInRoom(roomId, text);
      track('ui.clicked', { name: 'room_line_send', match_id: roomId });
      // Stream's copy of this line may have landed before this answer did (2026-10-21 Riley: the line showed twice
      // until a reload): the one already there stays, the answer's copy is not added on top of it
      setPage((cur) => (cur ? { ...cur, lines: cur.lines.some((l) => l.id === out.line.id) ? cur.lines : [...cur.lines, out.line] } : cur));
      return true;
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string; until?: string; resources?: string; message?: string } | null) : null;
      trackFailed('room_line_send', body?.error === 'text_rejected' ? 'text_rejected' : (body?.error || 'error'));
      // The sentence is the edge's (2026-10-22 Riley, rooms.js lineRefusalMessage); the line here is for an edge without one
      if (body?.error === 'text_rejected') toast.error(body.message || 'That line can’t go into the room as written. Say it another way.');
      else if (body?.error === 'muted') { toast.error('Ember has asked you to sit this day out.'); setPage((cur) => (cur ? { ...cur, me: { ...cur.me, muted_until: body.until || cur.me.muted_until } } : cur)); }
      else if (body?.error === 'text_too_long') toast.error(`That’s more than one line here holds (${room.line_max_chars} characters). Send it in two.`);
      // The line carried a risk to the person themselves (§5.4): it stays theirs, unsent, and the resources stand over the field until they write again.
      else if (body?.error === 'risk_to_self') setHeld(body.resources || '');
      // The check is down, not the line (2026-10-21): say so, not "couldn't send that"
      else if (body?.error === 'moderation_unavailable') toast.error('Couldn’t check that right now. Try again in a moment.');
      else toast.error('Couldn’t send that. Try again.');
      return false;
    }
  };

  const lineOf = (m: ConversationMessage): RoomLine | undefined => page.lines.find((l) => l.seq === m.turn_index);
  const reactable = (l: RoomLine | undefined): l is RoomLine => Boolean(l && (l.kind === 'human' || l.kind === 'host'));
  const reactionSet = page.reaction_set?.length ? page.reaction_set : QUICK_FALLBACK;
  // An emoji on a line, or theirs off it (需求四, 2026-10-19): drawn at once, the server's answer then stands; the room hears it as room_reaction.
  const react = async (line: RoomLine, emoji: string, from: 'bar' | 'grid' | 'capsule'): Promise<void> => {
    const current = line.reactions?.find((r) => r.mine)?.emoji ?? null;
    const next = current === emoji ? null : emoji;
    track('ui.clicked', { name: from === 'capsule' ? 'room_reaction_capsule' : 'room_reaction', emoji, state: next ? 'set' : 'removed', match_id: roomId });
    setPage((cur) => (cur ? { ...cur, lines: setLineReactions(cur.lines, line.id, (list) => withReaction(list, cur.me.name, next, cur.me.name)) } : cur));
    try {
      const out = await emberApi.reactInRoom(roomId, line.id, next);
      setPage((cur) => (cur ? { ...cur, lines: setLineReactions(cur.lines, line.id, () => out.reactions) } : cur));
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      trackFailed('room_reaction', body?.error || 'network_error', { match_id: roomId });
      setPage((cur) => (cur ? { ...cur, lines: setLineReactions(cur.lines, line.id, (list) => withReaction(list, cur.me.name, current, cur.me.name)) } : cur));
      toast.error(body?.error === 'room_closed' ? 'This room has closed.' : 'Couldn’t add that. Try again.');
    }
  };
  const readers = readersByLine(page.lines, members);
  const lineReactions = (m: ConversationMessage): ReactElement | null => {
    const line = lineOf(m);
    if (!reactable(line) || !line.reactions?.length) return null;
    return <RoomReactionCapsules reactions={line.reactions} roomId={roomId} align={line.mine ? 'end' : 'start'} onToggle={(emoji) => { void react(line, emoji, 'capsule'); }} />;
  };
  const lineReaders = (m: ConversationMessage): ReactElement | null => {
    const line = lineOf(m);
    return line ? <RoomReaders readers={readers.get(line.id) ?? []} /> : null;
  };
  // Sitting out, nothing of theirs reaches the room, an emoji included (the server refuses it): no React to offer.
  const onLineHold = (m: ConversationMessage, el: HTMLElement): void => {
    const line = lineOf(m);
    if (reactable(line) && !muted) setHolding({ line, el });
  };

  const lineActions = (m: ConversationMessage): ReactElement | null => {
    const line = lineOf(m);
    if (!reactable(line)) return null;
    // React (需求四): the quick bar a long press opens, for a mouse that has no long press — on Ember's lines too (2026-10-19 Riley: the apps could, the web could not be seen to).
    const reactBtn = muted ? null : <button type="button" className="convo-line-act" onClick={(e) => setHolding({ line, el: (e.currentTarget.closest('.convo-turn-body')?.querySelector('.convo-bubble-wrap') as HTMLElement | null) ?? e.currentTarget })}>React</button>;
    if (line.kind === 'host') return reactBtn;
    if (line.mine) {
      return <>{reactBtn}<button type="button" className="convo-line-act" data-log="room_line_delete_open" data-log-match-id={roomId} onClick={() => setDeleting(line)}>Delete</button></>;
    }
    // Report says this line should not be there — the word first. Block lives in the Apps only (2026-09-30 Riley); a block said there holds here too (me.blocked).
    return <>{reactBtn}<button type="button" className="convo-line-act" data-log="room_line_report_open" data-log-match-id={roomId} onClick={() => setReporting(line)}>Report</button></>;
  };
  const reportLine = async (line: RoomLine): Promise<void> => {
    setReporting(null);
    try { await emberApi.reportRoomLine(roomId, line.id); track('ui.clicked', { name: 'room_line_report', match_id: roomId }); setPage((cur) => (cur ? { ...cur, lines: cur.lines.filter((l) => l.id !== line.id) } : cur)); toast.info('Reported. Ember’s team will look at it.'); }
    catch { trackFailed('room_line_send', 'report_failed'); }
  };
  const deleteLine = async (line: RoomLine): Promise<void> => {
    setDeleting(null);
    try { await emberApi.deleteRoomLine(roomId, line.id); track('ui.clicked', { name: 'room_line_delete', match_id: roomId }); setPage((cur) => (cur ? { ...cur, lines: cur.lines.filter((l) => l.id !== line.id) } : cur)); }
    catch { trackFailed('room_line_send', 'delete_failed'); }
  };

  const saveName = async (): Promise<void> => {
    const next = (naming || '').trim();
    if (!next || busy) return;
    setBusy(true);
    try {
      const out = await emberApi.renameInRoom(roomId, next);
      track('ui.clicked', { name: 'room_rename', match_id: roomId });
      setPage((cur) => (cur ? { ...cur, me: { ...cur.me, name: out.name }, members: cur.members.map((x) => (x.me ? { ...x, name: out.name } : x)) } : cur));
      setNaming(null);
      setClash(false);
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      trackFailed('room_rename', body?.error || 'error');
      toast.error(body?.error === 'muted' ? 'Not while you’re sitting out. It can change once you’re back.' : body?.error === 'name_taken' ? 'That one’s taken here. Pick another.' : body?.error === 'text_rejected' ? 'That one can’t be a name here. Pick another.' : body?.error === 'name_too_long' ? `Keep it under ${room.name_max_chars} characters.` : body?.error === 'rename_limited' ? 'That’s ten name changes today. Try again tomorrow.' : 'Couldn’t change that. Try again.');
    } finally { setBusy(false); }
  };

  // The door (2026-10-01 二次拍板): going in is the first look — the room's cursor moves, the host is told
  // someone arrived — and it stays shut while someone seated already goes by this name.
  const saveIntro = async (): Promise<void> => {
    const next = (introing || '').trim();
    if (!next || busy) return;
    setBusy(true);
    try {
      const out = await emberApi.setRoomIntro(roomId, next);
      track('ui.clicked', { name: 'room_intro_edit', match_id: roomId });
      setPage((cur) => (cur ? { ...cur, me: { ...cur.me, intro: out.intro } } : cur));
      setIntroing(null);
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      trackFailed('room_intro_edit', body?.error || 'error');
      toast.error(body?.error === 'muted' ? 'Not while you’re sitting out. It can change once you’re back.' : body?.error === 'intro_too_long' ? 'A little shorter, please.' : body?.error === 'risk_to_self' ? 'Ember kept that one out of the room. It’s in your own conversation.'
        : 'That one can’t go in. Try other words.');
    } finally { setBusy(false); }
  };

  const enter = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await emberApi.roomSeen(roomId);
      track('ui.clicked', { name: 'room_enter', match_id: roomId });
      notifyKnocksChanged();
      setPage((cur) => (cur ? { ...cur, me: { ...cur.me, seen: true } } : cur));
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      trackFailed('room_enter', body?.error || 'error');
      if (body?.error === 'name_taken') setClash(true);
      else toast.error('Couldn’t open the door. Try again.');
    } finally { setBusy(false); }
  };

  const leave = async (takeLines: boolean): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await emberApi.leaveRoom(roomId, takeLines);
      track('ui.clicked', { name: 'room_leave_confirm', state: takeLines ? 'taken' : 'kept', match_id: roomId });
      notifyKnocksChanged();
      // The room stays on the list, greyed, with what is left of it there (2026-10-20).
      navigate('/chats', { replace: true });
    } catch { trackFailed('room_leave', 'error'); setLeaving(false); setBusy(false); }
  };

  if (!me.seen) {
    return (
      <div className="room-thread is-door">
        <div className="room-door" role="group" aria-label="Before you go in" data-log-seen="room_door" data-log-match-id={roomId}>
          <span className="room-door-kicker">Before you go in</span>
          <div className="room-door-avatar"><PresenceAvatar avatarKey={me.avatar} size={96} /></div>
          <p className="room-door-lead">Here you go by</p>
          {naming === null ? (
            <div className="room-door-name">
              <span className="room-door-name-word" dir="auto">{me.name}</span>
              <button type="button" className={`room-door-pencil${clash ? ' is-urgent' : ''}`} aria-label="Change my name" onClick={() => setNaming(me.name)}>
                <svg viewBox="0 0 20 20" width="21" height="21" aria-hidden><path d="M3 14.5V17h2.5L14 8.5 11.5 6 3 14.5zM12.9 4.6l2.5 2.5 1.2-1.2a.9.9 0 0 0 0-1.3l-1.2-1.2a.9.9 0 0 0-1.3 0l-1.2 1.2z" fill="currentColor"/></svg>
              </button>
            </div>
          ) : (
            <form className="room-door-form" onSubmit={(e) => { e.preventDefault(); void saveName(); }}>
              <input className="room-door-field" value={naming} maxLength={room.name_max_chars} onChange={(e) => setNaming(e.target.value)} aria-label="Your name in rooms" autoFocus />
              <div className="room-door-form-acts">
                <button type="submit" className="room-thread-btn" disabled={busy || !naming.trim()}>Save</button>
                <button type="button" className="room-thread-link" onClick={() => setNaming(null)}>Keep {me.name}</button>
              </div>
            </form>
          )}
          <p className="room-door-note">Never your real one. Change it any time.</p>
          {clash && <p className="room-door-clash" role="alert">Someone in this room already goes by {me.name}. Pick another name to go in.</p>}
          {/* The line on their seat (2026-10-04 拍板, 二次拍板 the same day; mock room-seat-sentence-2026-10-04): read here first, theirs to change, Go in is the yes to it; the others read it on the seat and once when they come in — Ember never says it. */}
          {me.intro && (
            <div className="room-door-intro">
              <div className="room-door-intro-lead">
                <span>The others will see this on your seat</span>
                {introing === null && (
                  <button type="button" className="room-door-pencil is-small" aria-label="Change what's on my seat" onClick={() => setIntroing(me.intro || '')}>
                    <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden><path d="M3 14.5V17h2.5L14 8.5 11.5 6 3 14.5zM12.9 4.6l2.5 2.5 1.2-1.2a.9.9 0 0 0 0-1.3l-1.2-1.2a.9.9 0 0 0-1.3 0l-1.2 1.2z" fill="currentColor"/></svg>
                  </button>
                )}
              </div>
              {introing === null ? (
                <p className="room-door-intro-paper" dir="auto">{me.intro}</p>
              ) : (
                <form className="room-door-intro-form" onSubmit={(e) => { e.preventDefault(); void saveIntro(); }}>
                  <textarea className="room-door-intro-field" value={introing} maxLength={300} onChange={(e) => setIntroing(e.target.value)} aria-label="What's on your seat" autoFocus />
                  <div className="room-door-form-acts">
                    <button type="submit" className="room-thread-btn" disabled={busy || !introing.trim()}>Save</button>
                    <button type="button" className="room-thread-link" onClick={() => setIntroing(null)}>Keep it</button>
                  </div>
                </form>
              )}
            </div>
          )}
          <button type="button" className="ember-btn ember-btn-primary room-door-go" disabled={busy || naming !== null || introing !== null} onClick={() => { void enter(); }}>Go in</button>
        </div>
      </div>
    );
  }

  const timer = roomTimer(room);
  return (
    <div className="room-thread">
      <div className="room-thread-head">
        <div className="room-thread-clock">
          <span className={`room-thread-timer${timer?.urgent ? ' is-urgent' : ''}`}>{timer?.text ?? 'open'}</span>
          <span className="room-rules">
            <button type="button" className={`room-rules-q${rules ? ' is-open' : ''}`} aria-expanded={rules} aria-label={pair ? 'How this chat works' : 'How this room works'}
              data-log="room_rules_open" data-log-state={rules ? 'close' : 'open'} data-log-match-id={roomId} onClick={() => setRules(!rules)}>?</button>
            {rules && <span className="room-rules-bubble" role="note" onClick={() => setRules(false)}>{pair ? PAIR_RULES : ROOM_RULES}</span>}
          </span>
        </div>
        <button type="button" className={`room-thread-who${open ? ' is-open' : ''}`} aria-expanded={open} data-log="room_head_open" data-log-state={open ? 'close' : 'open'} data-log-match-id={roomId} onClick={() => setOpen(!open)}>
          {/* The faces are the bar's (TalkPageHeader, 2026-10-20): this row is who and the way into the fold. */}
          <span className="room-thread-names" dir="auto"><b>{pair ? room.name : names}</b> · with Ember</span>
          <span className="room-thread-chev" aria-hidden>›</span>
        </button>
        {open && (
          <div className="room-thread-fold">
            {/* The person's own name first (2026-09-30 拍板): handed out, theirs to change in one tap; the fold is open on the first visit, so this is the first thing read. */}
            <div className="room-thread-mine">
              {naming === null ? (
                <p className="room-thread-mine-line" dir="auto">{pair ? <>In this chat you go by <b>{me.name}</b>. </> : <>In rooms you go by <b>{me.name}</b>, never your real name. </>}<button type="button" className="room-thread-link" onClick={() => setNaming(me.name)}>Pick another name</button></p>
              ) : (
                <form className="room-thread-name" onSubmit={(e) => { e.preventDefault(); void saveName(); }}>
                  <input className="room-thread-name-field" value={naming} maxLength={room.name_max_chars} onChange={(e) => setNaming(e.target.value)} aria-label="Your name in this room" autoFocus />
                  <button type="submit" className="room-thread-btn" disabled={busy || !naming.trim()}>Save</button>
                  <button type="button" className="room-thread-link" onClick={() => setNaming(null)}>Keep {me.name}</button>
                </form>
              )}
            </div>
            {members.map((m) => (
              <div key={m.name} className={`room-thread-seat${m.me ? ' is-me' : ''}`}>
                <PresenceAvatar avatarKey={m.avatar} size={36} />
                <div className="room-thread-seat-body">
                  <div className="room-thread-seat-name" dir="auto">{m.name}{m.me && <small>you</small>}</div>
                  {m.intro && <p className="room-thread-seat-intro" dir="auto">{m.intro}</p>}
                </div>
              </div>
            ))}
            <div className="room-thread-why">
              {/* A topic room was put up by its people on a topic (2026-10-26), not brought together by Ember. */}
              <span className="room-thread-why-head">{room.kind === 'topic' ? 'This room is about' : 'Why Ember brought you together'}</span>
              <p className="room-thread-reason" dir="auto">{room.kind === 'topic' ? room.topic : room.fork}</p>
            </div>
            {/* How it goes, apart from why (2026-10-27 walk-through: inside the why, in its type, it read as Ember's reason). */}
            <p className="room-thread-only">{pair
              ? 'You two come by at your own hours. Say what you like and come back when you like; Ember lets you know when they answer.'
              : 'People come by at their own hours. Say what you like and come back when you like; no one needs to be here at the same time, and Ember lets you know when someone answers.'}</p>
            <div className="room-thread-foot">
              {!leaving && <button type="button" className="room-thread-link" data-log="room_leave_open" data-log-match-id={roomId} onClick={() => setLeaving(true)}>{pair ? 'Leave this chat' : 'Leave this room'}</button>}
              {members.length > 0 && <span className="room-thread-only">Only the {countWord(members.length)} of you and Ember can read this.</span>}
            </div>
            {leaving && (
              <div className="room-thread-leave" ref={leaveRef} role="group" aria-label={pair ? 'Leave this chat' : 'Leave this room'}>
                {/* Leaving a pair ends it for both (rooms.leave → endPair), said as the check-in's stop says it (2026-10-27 walk-through). */}
                {/* Leaving is for good, said before it happens (2026-10-27 walk-through: told only afterwards, by Ember). */}
                <p className="room-thread-only">{pair ? `It closes for both of you. ${others[0] || 'They'} only sees that it wrapped up.` : 'You won’t be able to come back in.'}</p>
                <button type="button" className="room-thread-btn" disabled={busy} onClick={() => { void leave(false); }}>Leave, keep my lines</button>
                <button type="button" className="room-thread-btn" disabled={busy} onClick={() => { void leave(true); }}>Leave, take my lines</button>
                <button type="button" className="room-thread-link" onClick={() => setLeaving(false)}>Cancel</button>
              </div>
            )}
          </div>
        )}
        {note && (
          <div className="room-note" role="status" data-log-seen="room_note" data-log-match-id={roomId}>
            <div className="room-note-top">
              <span className="room-note-flame" aria-hidden />
              <span className="room-note-kicker">From Ember</span>
              <button type="button" className="room-note-x" aria-label="Close" data-log="room_note" data-log-state="close" data-log-match-id={roomId} onClick={() => setNote(null)}>×</button>
            </div>
            <p className="room-note-body" dir="auto">{note.text}</p>
          </div>
        )}
      </div>
      <ConversationShell
        messages={page.lines.map((l) => toMessage(l, others))}
        // A room never shows Ember writing (2026-09-25): being woken is not a
        // promise to speak — the host may stay out of the way — so dots that
        // appear and then go would be a false start; and the field stays open
        // while it decides, this is a room, not a turn-taking pair.
        typing={false}
        disabled={muted}
        onSend={(text) => send(text)}
        draftKey={`room:${roomId}`}
        logRoomId={roomId}
        releaseAfterSend
        onStop={() => { /* never shown: typing is never true here */ }}
        photos={{ pending: [], remaining: 0, onPickPhoto: async () => { /* no photos in a room */ }, onRemovePhoto: () => { /* none */ } }}
        // The mic dictates into the field (2026-09-30; a dead button before): the same words-out as the talk composer, room-scoped
        voice={{
          transcribe: async (audio) => {
            try {
              const { text } = await emberApi.transcribeRoomAudio(roomId, audio);
              if (!text) { trackFailed('chat_transcribe', 'empty', { match_id: roomId }); toast.error("We couldn't make out any words in that one. Try once more?"); return null; }
              return text;
            } catch { trackFailed('chat_transcribe', 'network_error', { match_id: roomId }); toast.error("That didn't go through. Try again."); return null; }
          },
          onMicUnavailable: (reason) => {
            trackFailed('chat_transcribe', reason === 'denied' ? 'mic_denied' : 'mic_unsupported', { match_id: roomId });
            toast.error(reason === 'denied' ? 'We need microphone permission to hear you. Typing works just as well.' : "This browser can't record audio. Typing works just as well.");
          },
        }}
        viewer={{ avatarKey: me.avatar, onChangeAvatar: () => { setOpen(true); setNaming(me.name); } }}
        // Two new friends write to the other by name (2026-10-26 walk-through: "the room" for a chat of two)
        composerPlaceholder={pair && others[0] ? `Say something to ${others[0]}` : 'Say something to the room'}
        onKeystroke={live.keystroke}
        // Held / muted first; else who is typing (2026-09-30): the field's own line, gone when they stop. Never someone
        // they blocked (2026-10-27 Riley: Jack, blocked, still showed typing) — the names are the room's, as me.blocked's are.
        composerLine={held ? `This line stays with you, unsent. ${held}` : muted && me.muted_until ? `Ember has asked you to sit this day out · back ${whenBack(me.muted_until)}`
          : typingShown.length ? `${typingShown.join(', ')} ${typingShown.length > 1 ? 'are' : 'is'} typing…` : undefined}
        lineActions={lineActions}
        lineReactions={lineReactions}
        lineReaders={lineReaders}
        onLineHold={onLineHold}
        newAfter={newAfter}
      />
      {holding && (
        <RoomReactionBar anchor={holding.el} set={reactionSet} quick={page.reaction_quick ?? QUICK_COUNT} roomId={roomId}
          mine={page.lines.find((l) => l.id === holding.line.id)?.reactions?.find((r) => r.mine)?.emoji ?? null}
          onPick={(emoji, from) => { const line = page.lines.find((l) => l.id === holding.line.id); if (line) void react(line, emoji, from); }}
          onClose={() => setHolding(null)} />
      )}
      {reporting && (
        <Modal onClose={() => setReporting(null)} labelledBy="room-report-title" className="settings-bind-modal">
          <h2 id="room-report-title" className="settings-bind-title">Report this line?</h2>
          <p className="settings-bind-body">Ember’s team will look at this line and what it’s answering.</p>
          <div className="settings-bind-actions">
            <button type="button" className="ember-btn ember-btn-ghost" onClick={() => setReporting(null)}>Keep it</button>
            <button type="button" className="ember-btn ember-btn-primary" onClick={() => { void reportLine(reporting); }}>Report</button>
          </div>
        </Modal>
      )}
      {deleting && (
        <Modal onClose={() => setDeleting(null)} labelledBy="room-delete-title" className="settings-bind-modal">
          <h2 id="room-delete-title" className="settings-bind-title">Delete this line?</h2>
          <p className="settings-bind-body">It’s gone for everyone in the room. This can’t be undone.</p>
          <div className="settings-bind-actions">
            <button type="button" className="ember-btn ember-btn-ghost" onClick={() => setDeleting(null)}>Keep it</button>
            <button type="button" className="ember-btn ember-btn-primary" onClick={() => { void deleteLine(deleting); }}>Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
